import {motion} from 'framer-motion';
import {Hide, HStack, Icon, Show, Space, Text, VStack} from 'platform/foundation';
import styled from 'styled-components';

import {useLayoutEffect, useRef, useState} from 'react';

import {isNil} from 'ramda';

import {Nullish, suffixTestId, TestIdProps, useToggle} from 'shared/web';

import i18n from '../../i18n/i18n';
import {Button} from '../Button/Button';
import {FormattedComment} from '../FormattedComment/FormattedComment';
import {InspectionReport} from '../InspectionReport/InspectionReport';
import {getInspectionReport} from '../InspectionReport/utils/getInspectionReport';

interface CommentExpandableProps extends TestIdProps {
  comment?: string | Nullish;
}

export function CommentExpandable(props: CommentExpandableProps) {
  const textRef = useRef<HTMLDivElement | null>(null);
  const [isCommentExpanded, toggleComment] = useToggle();
  const [isExpandable, setIsExpandable] = useState(false);

  const report = getInspectionReport(props.comment);
  const {height, threshold} = isNil(report) ? COMMENT_SIZE : REPORT_SIZE;
  const currentHeight = isCommentExpanded ? 'auto' : height;

  useLayoutEffect(() => {
    const textHeight = textRef?.current?.offsetHeight;
    setIsExpandable((textHeight ?? 0) > threshold);
  }, [props.comment, threshold]);

  const text = (
    <>
      <div ref={textRef}>
        {isNil(report) ? (
          <FormattedComment comment={props.comment} data-testid={suffixTestId('comment', props)} />
        ) : (
          <InspectionReport report={report} data-testid={suffixTestId('comment', props)} />
        )}
      </div>
      <Show when={isExpandable}>
        <motion.div
          initial={false}
          animate={{
            opacity: isCommentExpanded ? 0 : 1,
          }}
        >
          <Overlay />
        </motion.div>
      </Show>
    </>
  );

  return (
    <VStack>
      {/* The report carries its own headings, so the generic comment title is left out. */}
      <Show when={isNil(report)}>
        <HStack spacing={4} align="center">
          <Icon value="comment" size={6} color="accent.base" />
          <Text size="base">{i18n.t('comment')}</Text>
        </HStack>
        <Space vertical={4} />
      </Show>
      <Show when={isExpandable}>
        <motion.div
          initial={false}
          style={{
            overflow: 'hidden',
            position: 'relative',
          }}
          animate={{
            height: isExpandable ? currentHeight : 'auto',
          }}
        >
          {text}
        </motion.div>
      </Show>
      <Hide when={isExpandable}>{text}</Hide>
      <Show when={isExpandable}>
        <Space vertical={isCommentExpanded ? 3 : 0} />
        <Button
          variant="outline"
          onClick={toggleComment}
          leftIcon={isCommentExpanded ? 'arrowUp' : 'arrowDown'}
          title={isCommentExpanded ? i18n.t('hideText') : i18n.t('showText')}
          data-testid={suffixTestId('button-expandable', props)}
        />
      </Show>
    </VStack>
  );
}

const Overlay = styled.div`
  position: absolute;
  inset: 50% 0 0;
  background: rgb(2 0 36);
  background: linear-gradient(180deg, rgb(2 0 36 / 0%) 0%, rgb(255 255 255 / 100%) 100%);
`;

const COMMENT_SIZE = {height: 170, threshold: 250};
// Collapsed, the report shows the verdict and the start of the first section.
const REPORT_SIZE = {height: 320, threshold: 420};
